import { ref, watch } from "vue"

import { getBoundingBoxes, getTopHighlightBlockId } from "./page-helper"

const preparedScales = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5]

const getScaleOfZoomBox = ({ boundingBox, containerWidth, containerHeight }) => {
  const boxWidth = boundingBox.boundingBox[1].x - boundingBox.boundingBox[0].x
  const boxHeight = boundingBox.boundingBox[3].y - boundingBox.boundingBox[0].y

  let scaleOfZoomBox = (containerWidth * 0.4) / boxWidth
  if (containerHeight < boxHeight * scaleOfZoomBox) {
    scaleOfZoomBox = (containerHeight * 0.4) / boxHeight
  }

  // When scale is over 200 we need to reset the scale to 125
  if (scaleOfZoomBox > 2) {
    scaleOfZoomBox = 1.25
  }

  return scaleOfZoomBox
}

export const setupScaling = ({
  isShowExtractedWords,
  isForceShowHighlightBlocks,
  seeSourceAttributeSlug,
  initialScale,
  containerState,
  currentPageState
}) => {
  const scale = ref(undefined)
  const fitMode = ref(undefined)
  const isFitting = ref(undefined)

  const zoomOut = () => {
    if (typeof scale.value === "undefined") return

    const newScale = preparedScales.findLast(s => s < scale.value)
    if (!newScale) return

    scale.value = newScale
    isFitting.value = false
  }

  const zoomIn = () => {
    if (typeof scale.value === "undefined") return

    const newScale = preparedScales.find(s => s > scale.value)
    if (!newScale) return

    scale.value = newScale
    isFitting.value = false
  }

  const fitDocument = () => {
    if (typeof scale.value === "undefined") return

    switch (fitMode.value) {
      case "width":
        scale.value = containerState.height.value / currentPageState.height.value
        fitMode.value = "height"
        isFitting.value = true
        break
      case "height":
        scale.value = containerState.width.value / currentPageState.width.value
        fitMode.value = "width"
        isFitting.value = true
        break
    }
  }

  const zoomOutContinuous = () => {
    if (typeof scale.value === "undefined") return

    const newScale = (Math.round(scale.value * 100) - 1) / 100
    if (newScale < preparedScales[0]) return

    scale.value = newScale
    isFitting.value = false
  }

  const zoomInContinuous = () => {
    if (typeof scale.value === "undefined") return

    const newScale = (Math.round(scale.value * 100) + 1) / 100
    if (newScale > preparedScales[preparedScales.length - 1]) return

    scale.value = newScale
    isFitting.value = false
  }

  watch(
    [
      containerState.isResizing,
      currentPageState.positionalText,
      currentPageState.highlightBlockIds,
      currentPageState.highlightBlockScores,
      currentPageState.attributeSources,
      currentPageState.width,
      currentPageState.height
    ],
    ([
      containerIsResizing,
      currentPagePositionalText,
      currentPageHighlightBlockIds,
      currentPageHighlightBlockScores,
      currentPageAttributeSources,
      currentPageWidth,
      currentPageHeight
    ]) => {
      if (containerIsResizing) return

      if (
        typeof currentPagePositionalText === "undefined" ||
        typeof currentPageHighlightBlockIds === "undefined" ||
        typeof currentPageHighlightBlockScores === "undefined" ||
        typeof currentPageAttributeSources === "undefined" ||
        typeof currentPageWidth === "undefined" ||
        currentPageWidth === 0 ||
        typeof currentPageHeight === "undefined" ||
        currentPageHeight === 0
      ) {
        return
      }

      if (typeof containerState.width.value === "undefined" || typeof containerState.height.value === "undefined") {
        return
      }

      const boundingBoxIdForZoom = (() => {
        // always zoom if attributeSources are present
        if (currentPageAttributeSources.length > 0) {
          return currentPageAttributeSources.reduce((acc, current) => {
            if (current.ocr_block_score > acc.ocr_block_score) {
              return current
            }
            return acc
          }).ocr_block_id
        }

        // Do not zoom if scale has already been calculated
        if (typeof scale.value !== "undefined") {
          return undefined
        }

        if (isForceShowHighlightBlocks.value) {
          return getTopHighlightBlockId({
            highlightBlockIds: currentPageState.highlightBlockIds.value,
            highlightBlockScores: currentPageState.highlightBlockScores.value
          })
        }
      })()

      const boundingBoxForZoom = (() => {
        if (!boundingBoxIdForZoom) {
          return undefined
        }

        const boundingBoxes = getBoundingBoxes({
          positionalText: currentPagePositionalText,
          highlightBlockIds: currentPageHighlightBlockIds,
          highlightBlockScores: currentPageHighlightBlockScores,
          attributeSources: currentPageAttributeSources,
          isShowExtractedWords: isShowExtractedWords.value,
          isForceShowHighlightBlocks: isForceShowHighlightBlocks.value,
          seeSourceAttributeSlug: seeSourceAttributeSlug.value
        })

        return boundingBoxes.find(({ id }) => id === boundingBoxIdForZoom)
      })()

      if (boundingBoxForZoom) {
        scale.value = getScaleOfZoomBox({
          boundingBox: boundingBoxForZoom,
          containerWidth: containerState.width.value,
          containerHeight: containerState.height.value
        })
        fitMode.value = "width"
        isFitting.value = false
        return
      }

      if (typeof scale.value !== "undefined") return

      if (typeof initialScale.value !== "undefined") {
        scale.value = initialScale.value
        fitMode.value = "width"
        isFitting.value = false
        return
      }

      scale.value = containerState.width.value / currentPageWidth
      fitMode.value = "width"
      isFitting.value = true
    },
    { immediate: true }
  )

  watch(
    [containerState.width, containerState.height],
    ([containerWidth, containerHeight]) => {
      if (typeof containerWidth === "undefined" || typeof containerHeight === "undefined") {
        return
      }

      if (
        typeof currentPageState.width.value === "undefined" ||
        currentPageState.width.value === 0 ||
        typeof currentPageState.height.value === "undefined" ||
        currentPageState.height.value === 0
      ) {
        return
      }

      if (typeof scale.value === "undefined") return

      let newScale
      if (!isFitting.value) {
        newScale = scale.value
      } else {
        switch (fitMode.value) {
          case "width":
            newScale = containerWidth / currentPageState.width.value
            break
          case "height":
            newScale = containerHeight / currentPageState.height.value
            if (currentPageState.width.value * newScale > containerWidth) {
              newScale = containerWidth / currentPageState.width.value
            }
            break
        }
      }
      scale.value = newScale
    },
    { immediate: true }
  )

  return {
    scale,
    fitMode,
    isFitting,
    zoomOut,
    zoomIn,
    fitDocument,
    zoomOutContinuous,
    zoomInContinuous
  }
}
